<template>
  <ResponsiveView desktop-only>
    <div class="min-h-screen bg-[#F7F9FC] flex flex-col lg:flex-row">
      <SidebarComponent class="hidden lg:block" />

      <div class="flex-1 flex flex-col">
        <HeaderComponent class="hidden lg:flex" />

        <main class="flex-1 px-4 sm:px-6 lg:px-10 py-6 lg:py-8">
          <div class="max-w-7xl mx-auto space-y-6">
            <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p class="text-[11px] font-semibold uppercase tracking-[0.3em] text-gray-400">
                  Admin Panel
                </p>
                <h1 class="text-3xl font-semibold text-[#1E1E1F]">
                  User Access Control
                </h1>
                <p class="text-sm text-gray-500">
                  Kelola role dan akses fitur pengguna dengan tampilan tabel seperti referensi.
                </p>
              </div>
              <div class="flex items-center gap-3 rounded-full bg-white border border-gray-200 px-4 py-2 shadow-sm">
                <i class="fas fa-user-shield text-blue-600" />
                <span class="text-sm font-semibold text-gray-700">Admin only</span>
              </div>
            </div>

            <div v-if="isAdmin" class="bg-white rounded-[24px] border border-gray-100 shadow-sm overflow-hidden">
              <div class="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <div class="flex items-center gap-3">
                  <div class="w-11 h-11 rounded-full bg-blue-50 text-blue-600 grid place-items-center">
                    <i class="fas fa-users-cog" />
                  </div>
                  <div>
                    <p class="text-sm font-semibold text-gray-900">
                      Daftar Pengguna
                    </p>
                    <p class="text-xs text-gray-500">
                      Edit role, custom quiz, share link, dan push public.
                    </p>
                  </div>
                </div>
                <div class="flex items-center gap-2">
                  <button
                    type="button"
                    class="h-10 w-10 grid place-items-center rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50"
                    :disabled="isLoadingList"
                    @click="refreshList"
                  >
                    <i :class="isLoadingList ? 'fas fa-spinner fa-spin' : 'fas fa-sync'" />
                  </button>
                </div>
              </div>

              <div class="flex flex-col gap-3 px-4 sm:px-6 py-4 border-b border-gray-50 bg-gray-50">
                <div class="flex flex-col sm:flex-row sm:items-center gap-3">
                  <input
                    v-model.trim="searchQuery"
                    type="text"
                    placeholder="Search name or email"
                    class="w-full sm:max-w-xs rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                    @keyup.enter="refreshList"
                  >
                  <div class="flex items-center gap-2">
                    <button
                      type="button"
                      class="rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-white transition"
                      :disabled="isLoadingList"
                      @click="refreshList"
                    >
                      Apply
                    </button>
                    <span
                      v-if="isLoadingList"
                      class="inline-flex items-center gap-2 text-xs text-gray-500"
                    >
                      <i class="fas fa-spinner fa-spin" />
                      Loading...
                    </span>
                  </div>
                </div>
              </div>

              <div class="hidden md:block overflow-x-auto rounded-b-[24px] w-full">
                <table class="min-w-[760px] lg:min-w-[900px] xl:min-w-[1040px] text-sm w-full">
                  <thead class="bg-[#F1F4F9] text-gray-500 text-xs uppercase tracking-wide whitespace-nowrap">
                    <tr>
                      <th class="px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          class="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          :checked="allSelected"
                          @change="toggleAll($event.target.checked)"
                        >
                      </th>
                      <th class="px-4 py-3 text-left font-semibold">
                        <span class="flex items-center gap-1">
                          Name
                          <i class="fas fa-sort text-[10px] text-gray-300" />
                        </span>
                      </th>
                      <th class="px-4 py-3 text-left font-semibold">
                        <span class="flex items-center gap-1">
                          Email
                          <i class="fas fa-sort text-[10px] text-gray-300" />
                        </span>
                      </th>
                      <th class="px-4 py-3 text-left font-semibold hidden lg:table-cell">
                        <span class="flex items-center gap-1">
                          Contact
                          <i class="fas fa-sort text-[10px] text-gray-300" />
                        </span>
                      </th>
                      <th class="px-4 py-3 text-center font-semibold">
                        <span class="flex items-center justify-center gap-1">
                          Acc Type
                          <i class="fas fa-sort text-[10px] text-gray-300" />
                        </span>
                      </th>
                      <th class="px-4 py-3 text-center font-semibold">
                        <span class="flex items-center justify-center gap-1">
                          Custom Quiz
                          <i class="fas fa-sort text-[10px] text-gray-300" />
                        </span>
                      </th>
                      <th class="px-4 py-3 text-center font-semibold">
                        <span class="flex items-center justify-center gap-1">
                          Share Link
                          <i class="fas fa-sort text-[10px] text-gray-300" />
                        </span>
                      </th>
                      <th class="px-4 py-3 text-center font-semibold">
                        <span class="flex items-center justify-center gap-1">
                          Push Public
                          <i class="fas fa-sort text-[10px] text-gray-300" />
                        </span>
                      </th>
                      <th class="px-4 py-3 text-center font-semibold whitespace-nowrap hidden xl:table-cell">
                        <span class="flex items-center justify-center gap-1">
                          Last Login
                          <i class="fas fa-sort text-[10px] text-gray-300" />
                        </span>
                      </th>
                      <th class="px-4 py-3 text-center font-semibold whitespace-nowrap hidden xl:table-cell">
                        <span class="flex items-center justify-center gap-1">
                          Affiliate ID
                          <i class="fas fa-sort text-[10px] text-gray-300" />
                        </span>
                      </th>
                      <th class="px-4 py-3 text-center font-semibold">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="user in users"
                      :key="user.id"
                      class="border-b border-gray-100 last:border-b-0 hover:bg-gray-50/60 transition"
                    >
                      <td class="px-4 py-4 align-middle">
                        <input
                          type="checkbox"
                          class="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          :checked="selectedIds.includes(user.id)"
                          @change="toggleRow(user.id, $event.target.checked)"
                        >
                      </td>
                      <td class="px-4 py-4 align-middle">
                        <div class="flex items-center gap-3">
                          <div class="w-10 h-10 rounded-full bg-gray-100 overflow-hidden grid place-items-center text-[11px] text-gray-500">
                            <img
                              v-if="user.avatar"
                              :src="user.avatar"
                              :alt="user.name"
                              class="w-full h-full object-cover"
                            >
                            <span v-else>{{ initials(user.name) }}</span>
                          </div>
                          <div>
                            <p class="text-sm font-semibold text-gray-900 break-words max-w-[180px]">
                              {{ user.name }}
                            </p>
                            <p class="text-[11px] text-gray-400">ID {{ user.id }}</p>
                          </div>
                        </div>
                      </td>
                      <td class="px-4 py-4 align-middle text-gray-700 break-words max-w-[210px]">
                        {{ user.email }}
                      </td>
                      <td class="px-4 py-4 align-middle text-gray-700 whitespace-nowrap hidden lg:table-cell">
                        {{ user.contact }}
                      </td>
                      <td class="px-4 py-4 align-middle text-center">
                        <span :class="roleBadgeClass(user.role)">
                          {{ user.role }}
                        </span>
                      </td>
                      <td class="px-4 py-4 align-middle text-center">
                        <span :class="statusPillClass(user.customQuiz)">
                          {{ user.customQuiz ? "Yes" : "No" }}
                        </span>
                      </td>
                      <td class="px-4 py-4 align-middle text-center">
                        <span :class="statusPillClass(user.shareLink)">
                          {{ user.shareLink ? "Yes" : "No" }}
                        </span>
                      </td>
                      <td class="px-4 py-4 align-middle text-center">
                        <span :class="statusPillClass(user.pushPublic)">
                          {{ user.pushPublic ? "Yes" : "No" }}
                        </span>
                      </td>
                      <td class="px-4 py-4 align-middle text-center text-gray-700 hidden xl:table-cell">
                        {{ user.lastLogin }}
                      </td>
                      <td class="px-4 py-4 align-middle text-center text-gray-700 hidden xl:table-cell">
                        {{ user.affiliateId }}
                      </td>
                      <td class="px-4 py-4 align-middle">
                        <div class="flex items-center justify-center gap-2">
                          <button
                            type="button"
                            class="p-2 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-50 transition"
                            :disabled="deletingUserId === user.id"
                            @click="removeUser(user.id)"
                          >
                            <i :class="deletingUserId === user.id ? 'fas fa-spinner fa-spin' : 'far fa-trash-alt'" />
                          </button>
                          <button
                            type="button"
                            class="p-2 rounded-full text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition"
                            @click="openEditModal(user)"
                          >
                            <i class="fas fa-pen" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Card layout for narrower widths (still desktop view) -->
              <div class="md:hidden grid grid-cols-1 gap-3 px-4 py-4">
                <div
                  v-for="user in users"
                  :key="`card-${user.id}`"
                  class="rounded-2xl border border-gray-100 bg-white shadow-sm p-4 space-y-3"
                >
                  <div class="flex items-center justify-between gap-3">
                    <div class="flex items-center gap-3">
                      <div class="w-10 h-10 rounded-full bg-gray-100 overflow-hidden grid place-items-center text-[11px] text-gray-500">
                        <img
                          v-if="user.avatar"
                          :src="user.avatar"
                          :alt="user.name"
                          class="w-full h-full object-cover"
                        >
                        <span v-else>{{ initials(user.name) }}</span>
                      </div>
                      <div>
                        <p class="text-sm font-semibold text-gray-900">{{ user.name }}</p>
                        <p class="text-[11px] text-gray-400">ID {{ user.id }}</p>
                      </div>
                    </div>
                    <span :class="roleBadgeClass(user.role)">
                      {{ user.role }}
                    </span>
                  </div>

                  <div class="grid grid-cols-2 gap-2 text-sm text-gray-700">
                    <div class="space-y-1">
                      <p class="text-[11px] text-gray-400">Email</p>
                      <p>{{ user.email }}</p>
                    </div>
                    <div class="space-y-1">
                      <p class="text-[11px] text-gray-400">Contact</p>
                      <p>{{ user.contact }}</p>
                    </div>
                    <div class="space-y-1">
                      <p class="text-[11px] text-gray-400">Affiliate ID</p>
                      <p>{{ user.affiliateId }}</p>
                    </div>
                    <div class="space-y-1">
                      <p class="text-[11px] text-gray-400">Last Login</p>
                      <p>{{ user.lastLogin }}</p>
                    </div>
                  </div>

                  <div class="grid grid-cols-3 gap-2">
                    <span :class="statusPillClass(user.customQuiz)">
                      Custom Quiz {{ user.customQuiz ? 'Yes' : 'No' }}
                    </span>
                    <span :class="statusPillClass(user.shareLink)">
                      Share Link {{ user.shareLink ? 'Yes' : 'No' }}
                    </span>
                    <span :class="statusPillClass(user.pushPublic)">
                      Push Public {{ user.pushPublic ? 'Yes' : 'No' }}
                    </span>
                  </div>

                  <div class="flex items-center justify-end gap-2 pt-1">
                    <button
                      type="button"
                      class="p-2 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-50 transition"
                      :disabled="deletingUserId === user.id"
                      @click="removeUser(user.id)"
                    >
                      <i :class="deletingUserId === user.id ? 'fas fa-spinner fa-spin' : 'far fa-trash-alt'" />
                    </button>
                    <button
                      type="button"
                      class="p-2 rounded-full text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition"
                      @click="openEditModal(user)"
                    >
                      <i class="fas fa-pen" />
                    </button>
                  </div>
                </div>
              </div>

              <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 sm:px-6 py-4 border-t border-gray-100 bg-white">
                <div class="text-sm text-gray-600">
                  Page {{ pageState.page }} / {{ pageState.totalPages }} · {{ pageState.totalRecords }} users
                </div>
                <div class="flex items-center gap-2">
                  <button
                    type="button"
                    class="h-10 px-3 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    :disabled="isLoadingList || pageState.page <= 1"
                    @click="prevPage"
                  >
                    Prev
                  </button>
                  <select
                    v-model.number="pageState.pageSize"
                    class="h-10 rounded-lg border border-gray-200 px-3 text-sm text-gray-700 bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option :value="10">10</option>
                    <option :value="20">20</option>
                    <option :value="50">50</option>
                  </select>
                  <button
                    type="button"
                    class="h-10 px-3 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    :disabled="isLoadingList || pageState.page >= pageState.totalPages"
                    @click="nextPage"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>

            <div
              v-else
              class="bg-white rounded-[24px] border border-gray-100 shadow-sm p-10 text-center"
            >
              <div class="mx-auto w-16 h-16 rounded-full bg-blue-50 text-blue-600 grid place-items-center mb-4">
                <i class="fas fa-lock text-2xl" />
              </div>
              <h3 class="text-xl font-semibold text-gray-900 mb-2">
                Akses khusus admin
              </h3>
              <p class="text-sm text-gray-500 mb-6">
                Masuk sebagai admin untuk membuka menu ini.
              </p>
              <button
                type="button"
                class="px-5 py-3 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-500 transition"
                @click="goHome"
              >
                Kembali ke Dashboard
              </button>
            </div>
          </div>
        </main>
      </div>
    </div>
  </ResponsiveView>

  <ResponsiveView mobile-only>
    <div class="min-h-screen bg-[#F7F9FC] flex items-center justify-center px-6 text-center">
      <div class="bg-white rounded-[24px] border border-gray-100 shadow-sm p-8 space-y-3 max-w-sm">
        <div class="mx-auto w-12 h-12 rounded-full bg-gray-50 text-gray-500 grid place-items-center">
          <i class="fas fa-desktop" />
        </div>
        <h3 class="text-lg font-semibold text-gray-900">
          Desktop only
        </h3>
        <p class="text-sm text-gray-500">
          Menu User Access hanya tersedia pada tampilan desktop.
        </p>
      </div>
    </div>
  </ResponsiveView>

  <transition name="fade">
    <div
      v-if="showEditModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
    >
      <div class="w-full max-w-3xl rounded-3xl bg-white p-6 shadow-2xl">
        <div class="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p class="text-lg font-semibold text-gray-900">
              Edit Role
            </p>
          </div>
          <button
            type="button"
            class="text-gray-400 hover:text-gray-600"
            @click="closeEditModal"
          >
            <i class="fas fa-times" />
          </button>
        </div>

        <div class="my-5 border-t border-gray-200" />

        <div class="space-y-5">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label class="text-sm font-semibold text-gray-800 space-y-2">
              <span>Name</span>
              <input
                v-model="editForm.name"
                type="text"
                class="w-full rounded-xl border border-gray-200 px-4 py-3 text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              >
            </label>
            <label class="text-sm font-semibold text-gray-800 space-y-2">
              <span>Email</span>
              <input
                v-model="editForm.email"
                type="email"
                class="w-full rounded-xl border border-gray-200 px-4 py-3 text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              >
            </label>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label class="text-sm font-semibold text-gray-800 space-y-2">
              <span>Contact</span>
              <div class="flex items-center gap-2">
                <select
                  v-model="editForm.contactCode"
                  class="rounded-xl border border-gray-200 px-3 py-3 text-sm text-gray-800 bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option
                    v-for="option in countryCodes"
                    :key="option.code"
                    :value="option.code"
                  >
                    {{ option.label }}
                  </option>
                </select>
                <input
                  v-model="editForm.contactNumber"
                  type="text"
                  class="flex-1 rounded-xl border border-gray-200 px-4 py-3 text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  placeholder="0193857722"
                >
              </div>
            </label>
            <label class="text-sm font-semibold text-gray-800 space-y-2">
              <span>Affiliate ID</span>
              <input
                v-model="editForm.affiliateId"
                type="text"
                class="w-full rounded-xl border border-gray-200 px-4 py-3 text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              >
            </label>
          </div>

          <div class="pt-1 space-y-3">
            <p class="text-sm font-semibold text-gray-900">
              Acc Type
            </p>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="role in roleOptions"
                :key="role"
                type="button"
                class="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-semibold transition"
                :class="role === editForm.role
                  ? 'bg-blue-100 text-blue-700 border-blue-200'
                  : 'bg-gray-100 text-gray-600 border-gray-200 hover:border-gray-300'"
                @click="editForm.role = role"
              >
                <i class="fas fa-user" />
                {{ role }}
              </button>
            </div>
          </div>

          <div class="border-t border-gray-100" />

          <div class="space-y-5">
            <div class="flex items-center justify-between">
              <p class="text-base font-semibold text-gray-900">
                Custom Quiz
              </p>
              <div class="flex items-center gap-3">
                <span :class="editForm.customQuiz ? 'text-blue-600 font-semibold' : 'text-gray-400'">Yes</span>
                <button
                  type="button"
                  role="switch"
                  :aria-checked="editForm.customQuiz"
                  class="relative inline-flex h-6 w-11 items-center rounded-full transition"
                  :class="editForm.customQuiz ? 'bg-blue-500' : 'bg-gray-300'"
                  @click="editForm.customQuiz = !editForm.customQuiz"
                >
                  <span
                    class="inline-block h-5 w-5 transform rounded-full bg-white shadow transition"
                    :class="editForm.customQuiz ? 'translate-x-5' : 'translate-x-1'"
                  />
                </button>
                <span :class="!editForm.customQuiz ? 'text-blue-600 font-semibold' : 'text-gray-400'">No</span>
              </div>
            </div>

            <div class="flex items-center justify-between">
              <p class="text-base font-semibold text-gray-900">
                Share Link
              </p>
              <div class="flex items-center gap-3">
                <span :class="editForm.shareLink ? 'text-blue-600 font-semibold' : 'text-gray-400'">Yes</span>
                <button
                  type="button"
                  role="switch"
                  :aria-checked="editForm.shareLink"
                  class="relative inline-flex h-6 w-11 items-center rounded-full transition"
                  :class="editForm.shareLink ? 'bg-blue-500' : 'bg-gray-300'"
                  @click="editForm.shareLink = !editForm.shareLink"
                >
                  <span
                    class="inline-block h-5 w-5 transform rounded-full bg-white shadow transition"
                    :class="editForm.shareLink ? 'translate-x-5' : 'translate-x-1'"
                  />
                </button>
                <span :class="!editForm.shareLink ? 'text-blue-600 font-semibold' : 'text-gray-400'">No</span>
              </div>
            </div>

            <div class="flex items-center justify-between">
              <p class="text-base font-semibold text-gray-900">
                Push Public
              </p>
              <div class="flex items-center gap-3">
                <span :class="editForm.pushPublic ? 'text-blue-600 font-semibold' : 'text-gray-400'">Yes</span>
                <button
                  type="button"
                  role="switch"
                  :aria-checked="editForm.pushPublic"
                  class="relative inline-flex h-6 w-11 items-center rounded-full transition"
                  :class="editForm.pushPublic ? 'bg-blue-500' : 'bg-gray-300'"
                  @click="editForm.pushPublic = !editForm.pushPublic"
                >
                  <span
                    class="inline-block h-5 w-5 transform rounded-full bg-white shadow transition"
                    :class="editForm.pushPublic ? 'translate-x-5' : 'translate-x-1'"
                  />
                </button>
                <span :class="!editForm.pushPublic ? 'text-blue-600 font-semibold' : 'text-gray-400'">No</span>
              </div>
            </div>
          </div>

          <div class="border-t border-gray-100" />

          <div class="flex flex-col sm:flex-row sm:items-center gap-3">
            <button
              type="button"
              class="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-semibold text-red-600 hover:bg-red-100 transition"
              @click="selectedUser && removeUser(selectedUser.id)"
            >
              <i class="fas fa-trash-alt" />
              Delete User
            </button>
            <button
              type="button"
              class="flex-1 inline-flex items-center justify-center rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-500 transition"
              :disabled="isSaving"
              @click="saveEdit"
            >
              <span v-if="isSaving" class="inline-flex items-center gap-2">
                <i class="fas fa-spinner fa-spin" />
                Saving...
              </span>
              <span v-else>Save Changes</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { computed, reactive, ref, watch } from "vue";
import { useRouter } from "vue-router";
import HeaderComponent from "@/components/base/HeaderComponent.vue";
import SidebarComponent from "@/components/base/SidebarComponent.vue";
import ResponsiveView from "@/components/helper/ResponsiveView.vue";
import { useAuthStore } from "@/stores/authStore";
import {
  GetUserList,
  UpdateUserAccount,
  DeleteUserAccount,
} from "@/api/userApi";

const router = useRouter();
const auth = useAuthStore();

const currentRole = computed(() => {
  const type =
    auth.userType ??
    auth.user?.userType ??
    auth.user?.UserType ??
    "";
  return typeof type === "string" ? type.toLowerCase() : "";
});
const isAdmin = computed(() => currentRole.value === "admin");

const users = ref([
  // populated from API
]);

const selectedIds = ref([]);
const allSelected = computed(
  () => users.value.length > 0 && selectedIds.value.length === users.value.length
);

const roleOptions = ["Admin", "Tutor", "Student"];
const countryCodes = [
  { code: "+62", label: "🇮🇩 +62" },
  { code: "+60", label: "🇲🇾 +60" },
  { code: "+65", label: "🇸🇬 +65" },
  { code: "+1", label: "🇺🇸 +1" },
];

const editForm = reactive({
  name: "",
  email: "",
  contactCode: "+62",
  contactNumber: "",
  affiliateId: "",
  role: "Student",
  customQuiz: false,
  shareLink: false,
  pushPublic: false,
});

const selectedUser = ref(null);
const showEditModal = ref(false);
const isLoadingList = ref(false);
const isSaving = ref(false);
const deletingUserId = ref(null);
const searchQuery = ref("");
const pageState = reactive({
  page: 1,
  pageSize: 20,
  includeInactive: true,
  totalPages: 1,
  totalRecords: 0,
});

const toggleAll = (checked) => {
  selectedIds.value = checked ? users.value.map((user) => user.id) : [];
};

const toggleRow = (id, checked) => {
  if (checked) {
    selectedIds.value = Array.from(new Set([...selectedIds.value, id]));
  } else {
    selectedIds.value = selectedIds.value.filter((item) => item !== id);
  }
};

watch(
  () => users.value,
  (list) => {
    const idSet = new Set(list.map((user) => user.id));
    selectedIds.value = selectedIds.value.filter((id) => idSet.has(id));
  },
  { deep: true }
);
watch(
  () => pageState.pageSize,
  () => {
    pageState.page = 1;
    fetchUsers();
  }
);

const statusPillClass = (flag) =>
  flag
    ? "inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-600 border border-blue-100"
    : "inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-500 border border-red-100";

const roleBadgeClass = (role) => {
  const base =
    "inline-flex px-3 py-1 rounded-full text-xs font-semibold border";
  if (role?.toLowerCase() === "admin") {
    return `${base} bg-blue-50 text-blue-600 border-blue-100`;
  }
  if (role?.toLowerCase() === "tutor") {
    return `${base} bg-emerald-50 text-emerald-600 border-emerald-100`;
  }
  return `${base} bg-gray-50 text-gray-600 border-gray-200`;
};

const initials = (name = "") => {
  const parts = name
    .trim()
    .split(" ")
    .filter(Boolean);
  if (!parts.length) return "NA";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const parseContact = (raw = "") => {
  const fallback = { code: "+62", number: raw || "" };
  if (!raw || typeof raw !== "string") return fallback;
  if (raw.startsWith("+")) {
    const [code, ...rest] = raw.split(" ");
    return {
      code: code || "+62",
      number: rest.join(" ").trim(),
    };
  }
  return fallback;
};

const composeContact = (code, number) =>
  [code || "+62", number || ""].join(" ").trim();

const fetchUsers = async () => {
  isLoadingList.value = true;
  try {
    const response = await GetUserList({
      page: pageState.page,
      pageSize: pageState.pageSize,
      search: searchQuery.value,
      includeInactive: pageState.includeInactive,
    });
    const list = Array.isArray(response?.data) ? response.data : response || [];
    const total =
      response?.totalRecords ??
      response?.total ??
      response?.totalItems ??
      list.length;
    const totalPages =
      response?.totalPages ??
      response?.totalPage ??
      (total && pageState.pageSize
        ? Math.max(1, Math.ceil(total / pageState.pageSize))
        : 1);
    pageState.totalPages = Number.isFinite(totalPages) ? totalPages : 1;
    pageState.totalRecords = Number.isFinite(total) ? total : list.length;
    users.value = list.map((u, index) => ({
      id: u.id ?? u.userId ?? index,
      name: u.name ?? u.fullName ?? u.Name ?? "",
      email: u.email ?? u.Email ?? "",
      contact: u.phone ?? u.Phone ?? u.contact ?? "",
      role: u.userType ?? u.UserType ?? "User",
      customQuiz: Boolean(u.customQuiz ?? u.CustomQuiz ?? u.canCustomQuiz),
      shareLink: Boolean(u.shareLink ?? u.ShareLink ?? u.canShareLink),
      pushPublic: Boolean(u.pushPublic ?? u.PushPublic ?? u.canPushPublic),
      lastLogin: u.lastLogin ?? u.LastLogin ?? "",
      affiliateId: u.affiliateId ?? u.AffiliateId ?? "",
      avatar: u.avatar ?? u.Avatar ?? "",
      raw: u,
    }));
  } catch (error) {
    console.error("GetUserList failed:", error);
  } finally {
    isLoadingList.value = false;
  }
};

const openEditModal = (user) => {
  selectedUser.value = { ...user };
  editForm.name = user.name || "";
  editForm.email = user.email || "";
  const parsed = parseContact(user.contact);
  editForm.contactCode = parsed.code;
  editForm.contactNumber = parsed.number;
  editForm.affiliateId = user.affiliateId || "";
  editForm.role = user.role;
  editForm.customQuiz = user.customQuiz;
  editForm.shareLink = user.shareLink;
  editForm.pushPublic = user.pushPublic;
  showEditModal.value = true;
};

const closeEditModal = () => {
  showEditModal.value = false;
  selectedUser.value = null;
};

const saveEdit = () => {
  if (!selectedUser.value || isSaving.value) return;
  isSaving.value = true;
  UpdateUserAccount({
    userId: selectedUser.value.id,
    name: editForm.name,
    email: editForm.email,
    phone: composeContact(editForm.contactCode, editForm.contactNumber),
    userType: editForm.role?.toLowerCase?.() || editForm.role,
  })
    .then(() => fetchUsers())
    .catch((error) => {
      console.error("UpdateUserAccount failed:", error);
    })
    .finally(() => {
      isSaving.value = false;
      closeEditModal();
    });
};

const removeUser = (id) => {
  if (deletingUserId.value) return;
  deletingUserId.value = id;
  DeleteUserAccount({ userId: id })
    .then(() => {
      users.value = users.value.filter((user) => user.id !== id);
      if (selectedUser.value?.id === id) {
        closeEditModal();
      }
    })
    .catch((error) => {
      console.error("DeleteUserAccount failed:", error);
    })
    .finally(() => {
      deletingUserId.value = null;
    });
};

const refreshList = () => {
  pageState.page = 1;
  fetchUsers();
};

const goToPage = (page) => {
  const target = Math.min(Math.max(page, 1), pageState.totalPages || 1);
  if (target === pageState.page) return;
  pageState.page = target;
  fetchUsers();
};

const nextPage = () => goToPage(pageState.page + 1);
const prevPage = () => goToPage(pageState.page - 1);

const goHome = () => {
  router.push({ name: "Dashboard" });
};

watch(
  () => isAdmin.value,
  (val) => {
    if (val) fetchUsers();
  },
  { immediate: true }
);
</script>
